// One card per room in the instructor's room picker. Shows:
//   • Room name + color accent
//   • Status: Available / Live (someone else) / Yours
//   • Action button: Claim / Take over / Continue
//
// Claims are now manual-only: the Release button is the only way for an
// instructor to release a room. If a phone screen-locks and polling pauses,
// the room stays claimed (now-playing/queue go stale but voting still works).
// Take-over is the escape valve if someone needs to grab a stuck room.
//
// `last_polled_at` is still written by the poller for diagnostic purposes
// (instructors can see "last activity X minutes ago") but it doesn't gate
// any behavior anymore.

const ROOM_COLORS = {
  main: { bg: 'bg-peak-accent', border: 'border-peak-accent/40', text: 'text-peak-accent' },
  late: { bg: 'bg-peak-accent2', border: 'border-peak-accent2/40', text: 'text-peak-accent2' },
}

export default function RoomCard({
  roomId,
  roomConfig,
  myUserId,
  onClaim,
  onTakeOver,
  practiceLabel, // optional: "Practice mode" or similar tag
}) {
  const colors = ROOM_COLORS[roomId] || ROOM_COLORS.main
  const claim = roomConfig?.active_instructor || null
  const isMine = claim === myUserId
  const isAvailable = !claim
  const claimedAt = roomConfig?.claimed_at
  const lastPolled = roomConfig?.last_polled_at

  return (
    <div className={`card border-2 ${colors.border} space-y-3`}>
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-bold text-lg">{roomConfig?.name || roomId}</h3>
          {practiceLabel && (
            <span className={`pill ${colors.bg} text-white mt-1 inline-block`}>
              {practiceLabel}
            </span>
          )}
        </div>
        <RoomStatus mine={isMine} claim={claim} />
      </div>

      {claim && !isMine && (
        <div className="text-xs text-peak-muted space-y-1">
          <p>
            Currently running: <strong className="text-white">{roomConfig?.active_instructor_name || claim}</strong>
            {claimedAt && (
              <span> · since {new Date(claimedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
            )}
          </p>
          {lastPolled && (
            <p className="opacity-70">
              Last Spotify activity: {formatRelative(lastPolled)}
            </p>
          )}
        </div>
      )}

      {isMine ? (
        <button onClick={onClaim} className={`${colors.bg} text-white font-semibold w-full py-3 rounded-xl active:scale-95 transition-transform`}>
          Continue running this room
        </button>
      ) : isAvailable ? (
        <button onClick={onClaim} className={`${colors.bg} text-white font-semibold w-full py-3 rounded-xl active:scale-95 transition-transform`}>
          Claim this room
        </button>
      ) : (
        <button onClick={onTakeOver} className="btn-secondary w-full">
          Take over
        </button>
      )}
    </div>
  )
}

function RoomStatus({ mine, claim }) {
  if (mine) {
    return <span className="pill bg-peak-ok/20 text-peak-ok">🔵 Yours</span>
  }
  if (claim) {
    return <span className="pill bg-red-500/20 text-red-300">🔴 Live</span>
  }
  return <span className="pill bg-peak-ok/20 text-peak-ok">🟢 Available</span>
}

/** "5 min ago" / "just now" / "2 hr ago" — quick relative time formatter. */
function formatRelative(timestamp) {
  const ms = Date.now() - timestamp
  if (ms < 60_000) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  return `${hr} hr ago`
}
